/**
 * Shared Financial Calculation Utilities
 * Standardized functions for per-year growth calculations, projections, and financial metrics
 * Ensures consistency across Dashboard, Financial Statements, KPIs, and other output sections
 * Phase E.1 Implementation
 */

/**
 * Calculate projected units for a given year using per-year growth rates or single growth rate
 * Handles both annual_sales_volume_growth_rates (array/object) and annual_sales_volume_growth (single rate)
 * @param {Object} product - Product object with base_year_units and growth rate data
 * @param {number} targetYear - Year to calculate units for
 * @param {number} baseYear - Base year for calculations (default: 2025)
 * @returns {number} Projected units for target year
 */
export const calculateProjectedUnits = (product, targetYear, baseYear = 2025) => {
  const baseUnits = product.base_year_units || 0;
  
  if (baseUnits === 0) return 0;
  
  // Priority 1: Use per-year growth rates if available (array format)
  if (product.annual_sales_volume_growth_rates && Array.isArray(product.annual_sales_volume_growth_rates)) {
    let units = baseUnits;
    const rates = product.annual_sales_volume_growth_rates;
    
    for (let year = baseYear + 1; year <= targetYear; year++) {
      const yearIndex = year - (baseYear + 1); // Index for rates array
      const yearRate = rates[yearIndex] !== undefined ? rates[yearIndex] : 0;
      units = units * (1 + yearRate / 100);
    }
    return Math.round(units);
  }
  
  // Priority 2: Use per-year growth rates if available (object format - year as key)
  if (product.annual_sales_volume_growth_rates && typeof product.annual_sales_volume_growth_rates === 'object') {
    let units = baseUnits;
    
    for (let year = baseYear + 1; year <= targetYear; year++) {
      const yearRate = product.annual_sales_volume_growth_rates[year.toString()] || 0;
      units = units * (1 + yearRate / 100);
    }
    return Math.round(units);
  }
  
  // Priority 3: Use growth_rates_per_year if available (legacy format)
  if (product.growth_rates_per_year && typeof product.growth_rates_per_year === 'object') {
    let units = baseUnits;
    
    for (let year = baseYear + 1; year <= targetYear; year++) {
      const yearRate = product.growth_rates_per_year[year.toString()] || 0;
      units = units * (1 + yearRate / 100);
    }
    return Math.round(units);
  }
  
  // Fallback: Use single growth rate (compound annually)
  const yearsFromBase = targetYear - baseYear;
  const growthRate = product.annual_sales_volume_growth || 0;
  const projectedUnits = baseUnits * Math.pow(1 + (growthRate / 100), yearsFromBase);
  
  return Math.round(projectedUnits);
};

/**
 * Calculate projected revenue for a product in a given year
 * @param {Object} product - Product object
 * @param {number} targetYear - Year to calculate revenue for
 * @param {number} baseYear - Base year (default: 2025)
 * @returns {number} Projected revenue
 */
export const calculateProjectedRevenue = (product, targetYear, baseYear = 2025) => {
  const basePrice = product.base_price || 0;
  const projectedUnits = calculateProjectedUnits(product, targetYear, baseYear);
  
  return projectedUnits * basePrice;
};

/**
 * Calculate CAGR (Compound Annual Growth Rate) between two years
 * @param {number} startValue - Starting value
 * @param {number} endValue - Ending value
 * @param {number} numYears - Number of years
 * @returns {number} CAGR as percentage
 */
export const calculateCAGR = (startValue, endValue, numYears) => {
  if (startValue <= 0 || endValue <= 0 || numYears <= 0) return 0;
  
  const cagr = (Math.pow(endValue / startValue, 1 / numYears) - 1) * 100;
  return cagr;
};

/**
 * Calculate growth rate between two periods
 * @param {number} currentValue - Current period value
 * @param {number} previousValue - Previous period value
 * @returns {number} Growth rate as percentage
 */
export const calculateGrowthRate = (currentValue, previousValue) => {
  if (!previousValue || previousValue === 0) return 0;
  
  return ((currentValue - previousValue) / previousValue) * 100;
};

/**
 * Calculate margin percentage
 * @param {number} profit - Profit amount
 * @param {number} revenue - Revenue amount
 * @returns {number} Margin as percentage
 */
export const calculateMargin = (profit, revenue) => {
  if (!revenue || revenue === 0) return 0;
  
  return (profit / revenue) * 100;
};

/**
 * Calculate multiple financial margins from income statement data
 * @param {Object} incomeStatement - Income statement object
 * @returns {Object} Object containing all margin percentages
 */
export const calculateAllMargins = (incomeStatement) => {
  const revenue = incomeStatement.revenue || 0;
  
  return {
    grossMargin: calculateMargin(incomeStatement.gross_profit || 0, revenue),
    ebitdaMargin: calculateMargin(incomeStatement.ebitda || 0, revenue),
    ebitMargin: calculateMargin(incomeStatement.ebit || 0, revenue),
    netMargin: calculateMargin(incomeStatement.net_income || 0, revenue),
    operatingRatio: revenue > 0 ? ((incomeStatement.operating_expenses || 0) / revenue) * 100 : 0
  };
};

/**
 * Aggregate revenue from multiple products for a given year
 * @param {Array} products - Array of product objects
 * @param {number} targetYear - Year to calculate for
 * @param {string} filterBy - Optional filter (e.g., 'linked_revenue_stream_id')
 * @param {string} filterValue - Value to filter by
 * @returns {number} Total revenue
 */
export const aggregateProductRevenue = (products, targetYear, filterBy = null, filterValue = null) => {
  let filteredProducts = products;
  
  // Apply filter if specified
  if (filterBy && filterValue) {
    filteredProducts = products.filter(p => p[filterBy] === filterValue);
  }
  
  // Calculate total revenue
  return filteredProducts.reduce((sum, product) => {
    return sum + calculateProjectedRevenue(product, targetYear);
  }, 0);
};

/**
 * Calculate multi-year projections for a metric with growth rate
 * @param {number} baseValue - Starting value
 * @param {Array} years - Array of years to project
 * @param {number} growthRate - Annual growth rate as percentage
 * @param {number} baseYear - Base year
 * @returns {Array} Array of {year, value} objects
 */
export const projectMetric = (baseValue, years, growthRate, baseYear) => {
  return years.map(year => {
    const yearsFromBase = year - baseYear;
    const projectedValue = baseValue * Math.pow(1 + (growthRate / 100), yearsFromBase);
    
    return {
      year,
      value: projectedValue
    };
  });
};

/**
 * Calculate per-year growth rates from array of values
 * @param {Array} values - Array of {year, value} objects
 * @returns {Array} Array of {year, growthRate} objects
 */
export const calculatePerYearGrowthRates = (values) => {
  const growthRates = [];
  
  for (let i = 1; i < values.length; i++) {
    const current = values[i];
    const previous = values[i - 1];
    
    growthRates.push({
      year: current.year,
      growthRate: calculateGrowthRate(current.value, previous.value)
    });
  }
  
  return growthRates;
};

/**
 * Format currency with optional compact notation
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (default: 'USD')
 * @param {boolean} compact - Use compact notation (K, M, B)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = 'USD', compact = false) => {
  if (amount === null || amount === undefined) return '$0';
  
  const options = {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  };
  
  if (compact && Math.abs(amount) >= 1000) {
    options.notation = 'compact';
    options.compactDisplay = 'short';
  }
  
  return new Intl.NumberFormat('en-US', options).format(amount);
};

/**
 * Format percentage with specified decimal places
 * @param {number} value - Percentage value
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted percentage
 */
export const formatPercentage = (value, decimals = 1) => {
  if (value === null || value === undefined || isNaN(value)) return '0%';
  
  return `${value.toFixed(decimals)}%`;
};

/**
 * Calculate break-even point in units
 * @param {number} fixedCosts - Total fixed costs
 * @param {number} pricePerUnit - Price per unit
 * @param {number} variableCostPerUnit - Variable cost per unit
 * @returns {Object} { breakEvenUnits, breakEvenRevenue, contributionMargin }
 */
export const calculateBreakEven = (fixedCosts, pricePerUnit, variableCostPerUnit) => {
  const contributionMargin = pricePerUnit - variableCostPerUnit;
  
  if (contributionMargin <= 0) {
    return {
      breakEvenUnits: Infinity,
      breakEvenRevenue: Infinity,
      contributionMargin: 0,
      contributionMarginPercent: 0
    };
  }
  
  const breakEvenUnits = fixedCosts / contributionMargin;
  const breakEvenRevenue = breakEvenUnits * pricePerUnit;
  const contributionMarginPercent = (contributionMargin / pricePerUnit) * 100;
  
  return {
    breakEvenUnits: Math.ceil(breakEvenUnits),
    breakEvenRevenue,
    contributionMargin,
    contributionMarginPercent
  };
};

/**
 * Calculate margin of safety
 * @param {number} actualSales - Actual or projected sales
 * @param {number} breakEvenSales - Break-even sales
 * @returns {Object} { marginOfSafety, marginOfSafetyPercent }
 */
export const calculateMarginOfSafety = (actualSales, breakEvenSales) => {
  const marginOfSafety = actualSales - breakEvenSales;
  const marginOfSafetyPercent = actualSales > 0 ? (marginOfSafety / actualSales) * 100 : 0;
  
  return {
    marginOfSafety,
    marginOfSafetyPercent
  };
};

/**
 * Calculate financial ratios from balance sheet and income statement
 * @param {Object} balanceSheet - Balance sheet data
 * @param {Object} incomeStatement - Income statement data
 * @returns {Object} Object containing various financial ratios
 */
export const calculateFinancialRatios = (balanceSheet, incomeStatement) => {
  const revenue = incomeStatement.revenue || 0;
  const netIncome = incomeStatement.net_income || 0;
  const totalAssets = balanceSheet.total_assets || 0;
  const totalEquity = balanceSheet.total_equity || 0;
  const totalLiabilities = balanceSheet.total_liabilities || 0;
  
  return {
    // Profitability ratios
    returnOnAssets: totalAssets > 0 ? (netIncome / totalAssets) * 100 : 0,
    returnOnEquity: totalEquity > 0 ? (netIncome / totalEquity) * 100 : 0,
    profitMargin: revenue > 0 ? (netIncome / revenue) * 100 : 0,
    
    // Leverage ratios
    debtToEquity: totalEquity > 0 ? totalLiabilities / totalEquity : 0,
    debtToAssets: totalAssets > 0 ? (totalLiabilities / totalAssets) * 100 : 0,
    equityRatio: totalAssets > 0 ? (totalEquity / totalAssets) * 100 : 0,
    
    // Efficiency ratios
    assetTurnover: totalAssets > 0 ? revenue / totalAssets : 0
  };
};

/**
 * Smooth data for trend analysis (moving average)
 * @param {Array} data - Array of {year, value} objects
 * @param {number} window - Window size for moving average (default: 3)
 * @returns {Array} Smoothed data
 */
export const smoothTrendData = (data, window = 3) => {
  if (data.length < window) return data;
  
  return data.map((item, index) => {
    if (index < window - 1) return item;
    
    const windowData = data.slice(index - window + 1, index + 1);
    const average = windowData.reduce((sum, d) => sum + d.value, 0) / window;
    
    return {
      ...item,
      smoothedValue: average
    };
  });
};

/**
 * Calculate variance between actual and planned values
 * @param {number} actual - Actual value
 * @param {number} planned - Planned value
 * @returns {Object} { variance, variancePercent, status }
 */
export const calculateVariance = (actual, planned) => {
  const variance = actual - planned;
  const variancePercent = planned !== 0 ? (variance / planned) * 100 : 0;
  
  let status = 'on-track';
  if (Math.abs(variancePercent) > 20) {
    status = 'critical';
  } else if (Math.abs(variancePercent) > 10) {
    status = 'at-risk';
  }
  
  return {
    variance,
    variancePercent,
    status,
    favorable: variance >= 0
  };
};

/**
 * Helper to safely access nested object properties
 * @param {Object} obj - Object to access
 * @param {string} path - Dot-notation path (e.g., 'financial_statements.income_statement.revenue')
 * @param {*} defaultValue - Default value if path doesn't exist
 * @returns {*} Value at path or default
 */
export const getNestedValue = (obj, path, defaultValue = 0) => {
  const keys = path.split('.');
  let value = obj;
  
  for (const key of keys) {
    if (value && typeof value === 'object' && key in value) {
      value = value[key];
    } else {
      return defaultValue;
    }
  }
  
  return value !== undefined ? value : defaultValue;
};

export default {
  calculateProjectedUnits,
  calculateProjectedRevenue,
  calculateCAGR,
  calculateGrowthRate,
  calculateMargin,
  calculateAllMargins,
  aggregateProductRevenue,
  projectMetric,
  calculatePerYearGrowthRates,
  formatCurrency,
  formatPercentage,
  calculateBreakEven,
  calculateMarginOfSafety,
  calculateFinancialRatios,
  smoothTrendData,
  calculateVariance,
  getNestedValue
};
